import { stopIgnoring } from 'owa-locstrings/lib/strings/stopignoring.locstring.json';
import { ignore } from 'owa-locstrings/lib/strings/ignore.locstring.json';
import { unpin } from 'owa-locstrings/lib/strings/unpin.locstring.json';
import { snooze } from 'owa-locstrings/lib/strings/snooze.locstring.json';
import { categoryMenuItemMenuText } from 'owa-locstrings/lib/strings/categorymenuitemmenutext.locstring.json';
import {
    copy,
    openInNewTab,
    findEmailFromSender,
} from './getFilterableMailListContextMenuItems.locstring.json';
import { unflag } from 'owa-locstrings/lib/strings/unflag.locstring.json';
import { block } from 'owa-locstrings/lib/strings/block.locstring.json';
import { move } from 'owa-locstrings/lib/strings/move.locstring.json';
import { restoreItem } from 'owa-locstrings/lib/strings/restoreitem.locstring.json';
import { markAsNotJunk, markAsJunk, createTask } from '../strings.locstring.json';
import { markAsRead } from 'owa-locstrings/lib/strings/markasread.locstring.json';
import { flag } from 'owa-locstrings/lib/strings/flag.locstring.json';
import { pin } from 'owa-locstrings/lib/strings/pin.locstring.json';
import { archive } from 'owa-locstrings/lib/strings/archive.locstring.json';
import { deleteItem } from 'owa-locstrings/lib/strings/deleteitem.locstring.json';
import { showInImmersiveReader as showInImmersiveReader_1 } from 'owa-locstrings/lib/strings/showinimmersivereader.locstring.json';
import { itemHeaderTranslate } from 'owa-locstrings/lib/strings/itemheadertranslate.locstring.json';
import { createRule } from 'owa-locstrings/lib/strings/createrule.locstring.json';
import { assignPolicyMenuItemName } from 'owa-locstrings/lib/strings/assignpolicymenuitemname.locstring.json';
import { markAsUnread } from 'owa-locstrings/lib/strings/markasunread.locstring.json';
import { phishing } from 'owa-locstrings/lib/strings/phishing.locstring.json';
import { reportAbuse } from 'owa-locstrings/lib/strings/reportabuse.locstring.json';
import { itemHeaderForward } from 'owa-locstrings/lib/strings/itemheaderforward.locstring.json';
import { itemHeaderReplyAll } from 'owa-locstrings/lib/strings/itemheaderreplyall.locstring.json';
import { itemMailListHeaderViewMessageDetails } from './itemheaderviewmessagedetails.locstring.json';
import { itemMailListHeaderViewMessageSource } from './itemheaderviewmessagesource.locstring.json';
import { itemHeaderReply } from 'owa-locstrings/lib/strings/itemheaderreply.locstring.json';
import loc from 'owa-localize';
import {
    FilterableMenuItem,
    FilterableMenuItemArgs,
    ShouldShowBehavior,
} from 'owa-filterable-menu';
import { getCategoryMenuPropertiesForContextMenu } from 'owa-mail-category-view';
import createSnoozeSubMenu from 'owa-mail-datepicker/lib/utils/createSnoozeSubMenu';
import { getCopyToProperties, getMoveToPropertiesForContextMenu } from 'owa-mail-movetofolder-view';
import { lazyGetAssignPolicyPropertiesForMailContextMenu } from 'owa-mail-retention-policies';
import * as lazyTriageActions from 'owa-mail-triage-action';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { listViewStore } from 'owa-mail-list-store';
import {
    getMailMenuItemShouldShowMap,
    getMailMenuItemShouldDisable,
    MenuItemType,
} from 'owa-mail-filterable-menu-behavior';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import getSnoozeDateOptions from 'owa-mail-datepicker/lib/utils/getSnoozeDateOptions';
import * as clickHandlers from '../handlers/onClickHandlers';

let dividerKey = 0;
const contextMenuActionSource = 'ContextMenu';

/**
 * Get an array of filterable menu items for mail list context menu
 * @param rowKeys the row keys to show the context menu
 * @param tableViewId the id of the table that contains these rows
 * @return the context menu items
 */
export default function getFilterableMailListContextMenuItems(
    rowKeys: string[],
    tableViewId: string,
    onDismiss: (ev?: any) => void
): FilterableMenuItem[] {
    const {
        onSnoozeClicked,
        onReplyRow,
        onReplyAllRow,
        onViewMessageDetails,
        onForwardRow,
        onDeleteClicked,
        onArchiveClicked,
        onShowInImmersiveReaderClicked,
        onOpenInNewTabClicked,
        onMarkAsReadUnreadClicked,
        onFindEmailFromSenderClicked,
        onBlockClicked,
        markRowsAsFlagged,
        markRowsAsNotFlagged,
        onJunkNotJunkClicked,
        onIgnoreStopIgnoreClicked,
        markRowsPinnedUnpinned,
        onCreateRuleClicked,
        onTranslateClicked,
        onReportAbuseClicked,
        openTasksView,
        onMarkAsPhishingClicked,
    } = clickHandlers;

    const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();
    const snoozeDateOptions = getSnoozeDateOptions();

    const getAssignPolicyMenu = lazyGetAssignPolicyPropertiesForMailContextMenu.tryImportForRender();
    let assignPolicyMenuItem;
    if (getAssignPolicyMenu) {
        assignPolicyMenuItem = getAssignPolicyMenu(tableViewId, 'ContextLV' /* actionSource */);
    }

    const tableView = listViewStore.tableViews.get(tableViewId);
    return [
        // Reply/Reply all/Forward
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(itemHeaderReply),
            [FilterableMenuItemArgs.onClick]: () => onReplyRow(),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Reply],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(itemHeaderReplyAll),
            [FilterableMenuItemArgs.onClick]: () => onReplyAllRow(),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.ReplyAll],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(itemHeaderForward),
            [FilterableMenuItemArgs.onClick]: () => onForwardRow(),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Forward],
        }),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.Move]() ||
                shouldShowBehaviorMap[MenuItemType.Copy]() ||
                shouldShowBehaviorMap[MenuItemType.Delete]() ||
                shouldShowBehaviorMap[MenuItemType.Restore]() ||
                shouldShowBehaviorMap[MenuItemType.Archive]() ||
                shouldShowBehaviorMap[MenuItemType.EmptyFolder]() ||
                shouldShowBehaviorMap[MenuItemType.Categories]()
            );
        }),

        // Delete/Restore/Archive
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(deleteItem),
            [FilterableMenuItemArgs.onClick]: () => onDeleteClicked(rowKeys, tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Delete],
            [FilterableMenuItemArgs.disabled]: getMailMenuItemShouldDisable(MenuItemType.Delete),
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(restoreItem),
            [FilterableMenuItemArgs.onClick]: () =>
                lazyTriageActions.lazyRestoreMailListRowsAction.importAndExecute(
                    rowKeys,
                    tableViewId
                ),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Restore],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(archive),
            [FilterableMenuItemArgs.onClick]: () => onArchiveClicked(),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Archive],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(move),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Move],
            [FilterableMenuItemArgs.menuContent]: getMoveToPropertiesForContextMenu(
                tableViewId,
                onDismiss,
                contextMenuActionSource,
                shouldShowBehaviorMap,
                DirectionalHint.rightTopEdge
            ),
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(copy),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Copy],
            [FilterableMenuItemArgs.menuContent]: getCopyToProperties(
                tableViewId,
                onDismiss,
                contextMenuActionSource
            ),
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(categoryMenuItemMenuText),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Categories],
            [FilterableMenuItemArgs.menuContent]: getCategoryMenuPropertiesForContextMenu(
                tableViewId,
                onDismiss,
                'ContextLV',
                true /* shouldShowSearch */
            ),
        }),

        // Empty folder
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(deleteItem),
            [FilterableMenuItemArgs.onClick]: () => {
                const tableView = listViewStore.tableViews.get(tableViewId);

                lazyTriageActions.lazyEmptyTableView.importAndExecute(
                    tableView,
                    false /*isExplicitSoftDelete*/,
                    'ContextLV',
                    tableView.virtualSelectAllExclusionList
                );
            },
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.EmptyFolder],
        }),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.MarkAsRead]() ||
                shouldShowBehaviorMap[MenuItemType.MarkAsUnread]() ||
                shouldShowBehaviorMap[MenuItemType.Flag]() ||
                shouldShowBehaviorMap[MenuItemType.Unflag]() ||
                shouldShowBehaviorMap[MenuItemType.Pin]() ||
                shouldShowBehaviorMap[MenuItemType.Unpin]() ||
                shouldShowBehaviorMap[MenuItemType.CreateTask]()
            );
        }),

        // Mark as read/unread
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(markAsRead),
            [FilterableMenuItemArgs.onClick]: () =>
                onMarkAsReadUnreadClicked(tableViewId, true /* isReadValueSet */),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.MarkAsRead],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(markAsUnread),
            [FilterableMenuItemArgs.onClick]: () =>
                onMarkAsReadUnreadClicked(tableViewId, false /* isReadValueSet */),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.MarkAsUnread],
        }),

        // Snooze/Unsnooze
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(snooze),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Schedule],
            [FilterableMenuItemArgs.menuContent]: createSnoozeSubMenu(
                onSnoozeClicked,
                snoozeDateOptions,
                DirectionalHint.rightTopEdge
            ),
        }),

        // Flag/Unflag
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(flag),
            [FilterableMenuItemArgs.onClick]: () => markRowsAsFlagged(),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Flag],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(unflag),
            [FilterableMenuItemArgs.onClick]: () => markRowsAsNotFlagged(),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Unflag],
        }),

        // Pin/unpin
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(pin),
            [FilterableMenuItemArgs.onClick]: () =>
                markRowsPinnedUnpinned(rowKeys, tableViewId, true /* shouldMarkAsPinned */),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Pin],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(unpin),
            [FilterableMenuItemArgs.onClick]: () =>
                markRowsPinnedUnpinned(rowKeys, tableViewId, false /* shouldMarkAsPinned */),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Unpin],
        }),

        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(createTask),
            [FilterableMenuItemArgs.onClick]: () => openTasksView(rowKeys[0], tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.CreateTask],
        }),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.MarkAsJunk]() ||
                shouldShowBehaviorMap[MenuItemType.JunkEmailSafetyAction]() ||
                shouldShowBehaviorMap[MenuItemType.Ignore]() ||
                shouldShowBehaviorMap[MenuItemType.StopIgnoring]() ||
                shouldShowBehaviorMap[MenuItemType.Block]()
            );
        }),

        // Junk/Not junk/Block
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(markAsJunk),
            [FilterableMenuItemArgs.onClick]: () =>
                onJunkNotJunkClicked(rowKeys, tableViewId, true /* isJunkValueToSet */),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.MarkAsJunk],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(markAsNotJunk),
            [FilterableMenuItemArgs.onClick]: () =>
                onJunkNotJunkClicked(rowKeys, tableViewId, false /* isJunkValueToSet */),
            [FilterableMenuItemArgs.shouldShow]:
                shouldShowBehaviorMap[MenuItemType.JunkEmailSafetyAction],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(ignore),
            [FilterableMenuItemArgs.onClick]: () =>
                onIgnoreStopIgnoreClicked(rowKeys, tableView, true),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Ignore],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(reportAbuse),
            [FilterableMenuItemArgs.onClick]: () => onReportAbuseClicked(rowKeys[0], tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.ReportAbuse],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(phishing),
            [FilterableMenuItemArgs.onClick]: () => onMarkAsPhishingClicked(rowKeys, tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.MarkAsPhishing],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(stopIgnoring),
            [FilterableMenuItemArgs.onClick]: () =>
                onIgnoreStopIgnoreClicked(rowKeys, tableView, false),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.StopIgnoring],
        }),
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(block),
            [FilterableMenuItemArgs.onClick]: () => onBlockClicked(rowKeys, tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Block],
        }),

        newDivider(() => {
            return shouldShowBehaviorMap[MenuItemType.FindEmailFromSender]();
        }),

        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(findEmailFromSender),
            [FilterableMenuItemArgs.onClick]: () =>
                onFindEmailFromSenderClicked(rowKeys[0], tableViewId),
            [FilterableMenuItemArgs.shouldShow]:
                shouldShowBehaviorMap[MenuItemType.FindEmailFromSender],
        }),

        newDivider(() => {
            return (
                shouldShowBehaviorMap[MenuItemType.InboxRule]() ||
                shouldShowBehaviorMap[MenuItemType.Translate]() ||
                shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader]() ||
                shouldShowBehaviorMap[MenuItemType.ViewMessageDetails]()
            );
        }),

        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(createRule),
            [FilterableMenuItemArgs.onClick]: () => onCreateRuleClicked(rowKeys[0], tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.InboxRule],
        }),

        // Translate
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(itemHeaderTranslate),
            [FilterableMenuItemArgs.onClick]: () => onTranslateClicked(rowKeys, tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.Translate],
        }),

        // Show in immersive reader
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(showInImmersiveReader_1),
            [FilterableMenuItemArgs.onClick]: () => {
                onShowInImmersiveReaderClicked(rowKeys[0], tableViewId);
            },
            [FilterableMenuItemArgs.shouldShow]:
                shouldShowBehaviorMap[MenuItemType.ShowInImmersiveReader],
        }),

        // View Message Source or Details
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: isConsumer()
                ? loc(itemMailListHeaderViewMessageSource)
                : loc(itemMailListHeaderViewMessageDetails),
            [FilterableMenuItemArgs.onClick]: () => {
                onViewMessageDetails(rowKeys[0], tableViewId);
            },
            [FilterableMenuItemArgs.shouldShow]:
                shouldShowBehaviorMap[MenuItemType.ViewMessageDetails],
        }),

        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(openInNewTab),
            [FilterableMenuItemArgs.onClick]: () => onOpenInNewTabClicked(rowKeys, tableViewId),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.OpenInNewTab],
        }),

        newDivider(() => {
            return shouldShowBehaviorMap[MenuItemType.AssignPolicy]();
        }),

        // Assign Policy
        new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: loc(assignPolicyMenuItemName),
            [FilterableMenuItemArgs.shouldShow]: shouldShowBehaviorMap[MenuItemType.AssignPolicy],
            [FilterableMenuItemArgs.menuContent]: assignPolicyMenuItem,
        }),
    ];
}
/**
 * Create a divider which is used in the mail list context menu
 * @param shouldShow the should show behavior which indicates whether to show the context menu or not
 */
function newDivider(shouldShowBehavior?: ShouldShowBehavior): FilterableMenuItem {
    return new FilterableMenuItem({
        [FilterableMenuItemArgs.name]: '-',
        [FilterableMenuItemArgs.shouldShow]: shouldShowBehavior,
        [FilterableMenuItemArgs.key]: (dividerKey++).toString(),
    });
}
